/**
 * Blumen Client-Side Router
 *
 * Provides SPA navigation without full page reloads.
 * On the first load, SSR content is hydrated normally.
 * Subsequent navigations swap React components client-side
 * using the History API and the generated route map.
 */

import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useEffect,
	useRef,
	Suspense,
} from "react";
import {
	applyMetadata,
	BlumenErrorBoundary,
	consumeCached,
	DefaultLoading,
	matchRoute,
	type RouteDef,
} from "@blumentechorg/blumenjs/runtime";

// ── Context types ──────────────────────────────────────────────

interface RouterContextValue {
	/** Current URL pathname */
	path: string;
	/** Extracted dynamic params for the current route (e.g. { id: "42" }) */
	params: Record<string, string>;
	/** Programmatic navigation */
	navigate: (to: string) => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

// Exported for Link.tsx to do safe SSR checks without throwing
export { RouterContext as RouterContextRef };

/**
 * Hook for page components to access the router.
 *
 * @example
 * const { path, params, navigate } = useRouter();
 */
export function useRouter(): RouterContextValue {
	const ctx = useContext(RouterContext);
	if (!ctx) {
		throw new Error("useRouter must be used inside <RouterProvider>");
	}
	return ctx;
}

// ── Provider props ─────────────────────────────────────────────

interface RouterProviderProps {
	/** The auto-generated route definitions */
	routes: RouteDef[];
	/** The App wrapper component */
	App: React.ComponentType<any>;
	/** The NotFound component for unmatched routes */
	notFoundComponent: React.ComponentType<any>;
	/** Initial SSR props passed from the server */
	initialProps?: Record<string, any>;
	children?: never; // Provider renders its own tree
}

// ── Provider ───────────────────────────────────────────────────

export function RouterProvider({
	routes,
	App,
	notFoundComponent: NotFound,
	initialProps = {},
}: RouterProviderProps) {
	const [path, setPath] = useState<string>(
		() => window.location.pathname,
	);
	const [transitioning, setTransitioning] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [loadingComponent, setLoadingComponent] = useState<React.ComponentType<any> | null>(null);
	const [dynamicProps, setDynamicProps] = useState<Record<string, any>>({});

	// Track whether we're on the very first render (SSR hydration).
	// On the first render we re-use the server-provided props.
	const isFirstRender = useRef(true);

	// ── Route matching ──────────────────────────────────────────
	const match = matchRoute(path, routes);
	const PageComponent = match ? match.component : NotFound;
	const params = match ? match.params : {};

	// Build props for the page component
	const pageProps = isFirstRender.current
		? { ...initialProps, params: { ...(initialProps.params || {}), ...params } }
		: {
				...dynamicProps,
				path,
				params,
				query: {},
				timestamp: Date.now(),
				serverRendered: false,
			};

	// After the first render we stop using the SSR props
	useEffect(() => {
		isFirstRender.current = false;
	}, []);

	// ── Navigation function ─────────────────────────────────────
	const navigate = useCallback(
		(to: string) => {
			if (to === path) return; // no-op for same path

			// Animate out → change route → animate in
			setTransitioning(true);

			// Check prefetch cache first (populated by <Link> on hover)
			const cached = consumeCached(to);

			// Determine the loading component for the target route
			const targetMatch = matchRoute(to, routes);
			const targetLoading = targetMatch?.loading || null;

			// If we don't have cached data and route has a loading component, show it
			if (!cached && targetLoading) {
				setLoadingComponent(() => targetLoading);
				setIsLoading(true);
			}

			// Fetch dynamic data from Go server (or use cached data)
			const fetchPromise = cached
				? Promise.resolve(cached)
				: fetch(to, {
					headers: { "X-Blumen-Data": "1" }
				}).then(res => res.ok ? res.json() : {}).catch(err => {
					console.error("Failed to fetch route data", err);
					return {};
				});

			// Allow the exit transition to play (150ms)
			const delayPromise = new Promise(resolve => setTimeout(resolve, 150));

			Promise.all([fetchPromise, delayPromise]).then(([newData]: any[]) => {
				if (newData?.__redirect) {
					window.location.href = newData.__redirect;
					return;
				}

				setDynamicProps(newData);

				// Update <head> metadata for the new page
				if (newData?.metadata) {
					applyMetadata(newData.metadata);
				}

				window.history.pushState(null, "", to);
				setPath(to);
				window.scrollTo(0, 0);

				// Clear loading state and transition
				requestAnimationFrame(() => {
					setIsLoading(false);
					setLoadingComponent(null);
					setTransitioning(false);
				});
			});
		},
		[path],
	);

	// ── Back / Forward button support ───────────────────────────
	useEffect(() => {
		const onPopState = () => {
			setTransitioning(true);
			const to = window.location.pathname;

			// Check prefetch cache for back/forward navigation
			const cached = consumeCached(to);

			// Show loading component if available
			if (!cached) {
				const targetMatch = matchRoute(to, routes);
				if (targetMatch?.loading) {
					setLoadingComponent(() => targetMatch.loading!);
					setIsLoading(true);
				}
			}

			const fetchPromise = cached
				? Promise.resolve(cached)
				: fetch(to, {
					headers: { "X-Blumen-Data": "1" }
				}).then(res => res.ok ? res.json() : {}).catch(() => ({}));
			
			const delayPromise = new Promise(resolve => setTimeout(resolve, 150));

			Promise.all([fetchPromise, delayPromise]).then(([newData]: any[]) => {
				if (newData?.__redirect) {
					window.location.href = newData.__redirect;
					return;
				}

				setDynamicProps(newData);
				setPath(to);

				// Update <head> metadata for the new page
				if (newData?.metadata) {
					applyMetadata(newData.metadata);
				}
				
				requestAnimationFrame(() => {
					setIsLoading(false);
					setLoadingComponent(null);
					setTransitioning(false);
				});
			});
		};

		window.addEventListener("popstate", onPopState);
		return () => window.removeEventListener("popstate", onPopState);
	}, []);

	// Track whether hydration is complete. During hydration, we skip
	// Suspense because the lazy chunk hasn't loaded yet and Suspense
	// would show a fallback that mismatches the server HTML.
	const [hydrated, setHydrated] = useState(false);
	useEffect(() => {
		setHydrated(true);
	}, []);

	// ── Render ──────────────────────────────────────────────────
	const contextValue: RouterContextValue = { path, params, navigate };

	// Determine what to render:
	// 1. If loading — show the loading component
	// 2. Otherwise — show the page component
	const LoadingComp = loadingComponent;

	// During initial hydration, render App directly (no Suspense/ErrorBoundary)
	// to match the SSR output exactly. After hydration, wrap with Suspense
	// for lazy-loaded navigation.
	const pageContent = hydrated ? (
		<BlumenErrorBoundary>
			<Suspense fallback={LoadingComp ? <LoadingComp /> : <DefaultLoading />}>
				<App Component={PageComponent} pageProps={pageProps} />
			</Suspense>
		</BlumenErrorBoundary>
	) : (
		<App Component={PageComponent} pageProps={pageProps} />
	);

	return (
		<RouterContext.Provider value={contextValue}>
			{isLoading && LoadingComp ? (
				<div className="page-transition page-transition-active">
					<LoadingComp />
				</div>
			) : (
				<div
					className={`page-transition ${transitioning ? "page-transition-exit" : "page-transition-active"}`}
				>
					{pageContent}
				</div>
			)}
		</RouterContext.Provider>
	);
}
